import type {
  ClientErrorBreadcrumb,
  ClientErrorKind,
  ClientErrorPayload,
} from '@food-diary/shared';
import { API_BASE_URL } from './api';

// Global error reporter. Installed once from `main.tsx`. Catches errors
// that escape React (unhandled promise rejections, event handlers,
// callbacks, errors during initial mount) and beacons a structured
// payload to `POST /v1/client-errors`. The endpoint persists each report
// to the `client_errors` D1 table (cleaned up nightly) so we can chase
// up "I hit a bug yesterday" reports without needing wrangler tail.
//
// `reportClientError` and `addBreadcrumb` are also exported for the
// react-query global error handler, the React Router error boundary,
// and the api fetch wrapper — they all submit through the same
// rate-limited transport.

const MAX_REPORTS_PER_MINUTE = 10;
const WINDOW_MS = 60_000;
const BREADCRUMB_LIMIT = 10;

function stringifyReason(reason: unknown): string {
  if (reason instanceof Error) return reason.message;
  if (typeof reason === 'string') return reason;
  return 'unhandled rejection';
}

// Module-level state. `installed` guards against duplicate listeners
// (HMR, tests). `breadcrumbs` is a small ring buffer the reporter
// appends to every payload so we can see what the user did right
// before the crash. `timestamps` is the rate-limit window. All three
// are intrinsically mutable; the file's narrowly-scoped surface keeps
// the mutation contained.
/* eslint-disable functional/prefer-readonly-type */
const moduleState = {
  installed: false,
  breadcrumbs: [] as ClientErrorBreadcrumb[],
  timestamps: [] as number[],
};
/* eslint-enable functional/prefer-readonly-type */

function allowReport(): boolean {
  const now = Date.now();
  const ts = moduleState.timestamps;
  while (ts.length > 0 && now - (ts[0] ?? 0) > WINDOW_MS) {
    ts.shift();
  }
  if (ts.length >= MAX_REPORTS_PER_MINUTE) return false;
  ts.push(now);
  return true;
}

function appVersion(): string {
  // `__APP_VERSION__` is Vite-`define`d at build time. It's `'dev'`
  // when the build env doesn't set GIT_SHA / CF_PAGES_COMMIT_SHA.
  try {
    return typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : 'dev';
  } catch {
    return 'dev';
  }
}

function safeUrl(): string | undefined {
  try {
    return globalThis.location.href;
  } catch {
    return undefined;
  }
}

function safeRoute(): string | undefined {
  try {
    return globalThis.location.pathname;
  } catch {
    return undefined;
  }
}

function safeUserAgent(): string | undefined {
  try {
    return globalThis.navigator.userAgent;
  } catch {
    return undefined;
  }
}

function safeOnline(): boolean | undefined {
  try {
    return globalThis.navigator.onLine;
  } catch {
    return undefined;
  }
}

/**
 * Push a breadcrumb to the ring buffer. Keep `label` short (<200 chars)
 * — the route caps it server-side and we want the buffer compact.
 */
export function addBreadcrumb(kind: ClientErrorBreadcrumb['kind'], label: string): void {
  const trimmed = label.length > 200 ? label.slice(0, 200) : label;
  moduleState.breadcrumbs.push({ at: Date.now(), kind, label: trimmed });
  while (moduleState.breadcrumbs.length > BREADCRUMB_LIMIT) {
    moduleState.breadcrumbs.shift();
  }
}

/** Drop all breadcrumbs (test helper). */
export function __resetBreadcrumbs(): void {
  moduleState.breadcrumbs.length = 0;
}

/** Reset the rate-limit window (test helper). */
export function __resetRateLimit(): void {
  moduleState.timestamps.length = 0;
}

/** Snapshot the current breadcrumbs (test helper / introspection). */
export function getBreadcrumbs(): readonly ClientErrorBreadcrumb[] {
  return [...moduleState.breadcrumbs];
}

type PartialReport = Partial<ClientErrorPayload> &
  Pick<ClientErrorPayload, 'kind' | 'message'> & { readonly skipBreadcrumbs?: boolean };

function transmit(payload: ClientErrorPayload): void {
  const body = JSON.stringify(payload);
  console.error('[client-error]', payload);
  try {
    const beacon = navigator.sendBeacon.bind(navigator);
    const blob = new Blob([body], { type: 'application/json' });
    if (beacon(`${API_BASE_URL}/v1/client-errors`, blob)) return;
  } catch {
    // fall through to fetch
  }
  // Fallback: keepalive fetch so the request survives page unload.
  void fetch(`${API_BASE_URL}/v1/client-errors`, {
    method: 'POST',
    credentials: 'include',
    keepalive: true,
    headers: { 'Content-Type': 'application/json' },
    body,
  }).catch(() => {
    // Swallow — beacon is best-effort.
  });
}

/**
 * Send a structured error report. Used internally by the global
 * window.error / unhandledrejection handlers, and externally by the
 * react-query / React Router / api-fetch wiring. Always merges in
 * `route`, `userAgent`, `appVersion`, `online`, and the current
 * breadcrumb ring buffer.
 */
export function reportClientError(input: PartialReport): void {
  if (!allowReport()) return;
  const { skipBreadcrumbs, ...rest } = input;
  const breadcrumbs =
    skipBreadcrumbs || moduleState.breadcrumbs.length === 0
      ? undefined
      : [...moduleState.breadcrumbs];
  const payload: ClientErrorPayload = {
    timestamp: Date.now(),
    url: safeUrl(),
    route: safeRoute(),
    userAgent: safeUserAgent(),
    appVersion: appVersion(),
    online: safeOnline(),
    ...rest,
    ...(breadcrumbs ? { breadcrumbs } : {}),
  };
  transmit(payload);
}

export function installErrorReporter(): void {
  if (moduleState.installed) return;
  moduleState.installed = true;

  globalThis.addEventListener('error', (event) => {
    reportClientError({
      kind: 'error' satisfies ClientErrorKind,
      message: event.message || String(event.error ?? 'unknown error'),
      stack: event.error instanceof Error ? event.error.stack : undefined,
    });
  });

  globalThis.addEventListener('unhandledrejection', (event) => {
    const reason: unknown = event.reason;
    reportClientError({
      kind: 'unhandledrejection' satisfies ClientErrorKind,
      message: stringifyReason(reason),
      stack: reason instanceof Error ? reason.stack : undefined,
    });
  });
}
